import { combineReducers } from "redux";
import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import companySearchResultsReducer from "../../company-search/store/reducer";
import contactsSearchReducer from "../../contacts-search/store/reducer";
import profilesReducer from "../../contacts-search-profiles/store/reducer";
import type { CommonStateType } from "../types/common";
import type { AdvancedSearchState } from "../types/state";

import * as ac from "./action-creators";

const INITIAL_ADVANCED_SEARCH_COMMON_STATE: CommonStateType = {
    isClearFiltersModalOpened: false,
    isDescriptionFetched: false,
    isDescriptionFetching: true,
    isExpanded: true,
    isLoading: false,
    isSummaryOpened: false,
    isFilteringPausedOverlayOpen: false,
    isQuickSearchesOverlayOpened: false,
    isQuickSearchOverlayForceOpened: false,
    isSavedSearchReminderOverlayOpened: false,
    isSearchCreating: false,
    searchCreateError: undefined,
    isSearchUpdating: false,
    searchUpdateError: undefined,
    selectedFiltersTabIndex: 0,
    savedSearches: [],
    savedSearchesFetching: false,
    isSavedSearchesFetched: false,
    isSearchesModalOpened: false,
    isSaveSearchTriggerModalOpen: false,
    saveSearchModal: {
        isOpen: false,
        saveMode: "manual",
    },
    isSavedSearchDeleteModalOpened: false,
    isSearchDeleting: false,
    savedSearchToBeDeleted: null,
    currentSavedSearchObject: null,
    isSearchByIdFetching: false,
    searchByIdFetchError: undefined,
    isFooterDisabled: false,
    excelDownloading: false,
    excelDownloadError: undefined,
    isAggregatedDataUsed: true,
    isConnectedCrmClicked: false,
    isListPanelClicked: false,
    navigation: {
        activeElements: new Set<string>(),
    },
    searchQuota: {
        total: 0,
        remaining: 0,
        used: 0,
        isAvailable: false,
        isUnlimited: false,
        interval: 0,
    },
    isSearchQuotaLoading: false,
};

const commonReducer = createReducer<CommonStateType, ActionType<typeof ac>>(INITIAL_ADVANCED_SEARCH_COMMON_STATE)
    .handleAction(ac.updateSavedSearchNameAsync.success, (state, { payload }) => ({
        ...state,
        savedSearches: state.savedSearches.map((search) => {
            if (search.searchId === payload.searchId) {
                return {
                    ...search,
                    name: payload.name,
                };
            }

            return search;
        }),
        currentSavedSearchObject:
            state.currentSavedSearchObject?.searchId === payload.searchId
                ? { ...state.currentSavedSearchObject, name: payload.name }
                : state.currentSavedSearchObject,
    }))
    .handleAction(
        ac.deleteSavedSearchAsync.request,
        (state): CommonStateType => ({
            ...state,
            isSearchDeleting: true,
            searchDeleteError: INITIAL_ADVANCED_SEARCH_COMMON_STATE.searchDeleteError,
        }),
    )
    .handleAction(
        ac.deleteSavedSearchAsync.success,
        (state, { payload }): CommonStateType => ({
            ...state,
            isSearchDeleting: false,
            isSavedSearchDeleteModalOpened: false,
            savedSearches: state.savedSearches.map((search) => {
                if (search.searchId === payload) {
                    return {
                        ...search,
                        markedAsDeleted: true,
                    };
                }

                return search;
            }),
        }),
    )
    .handleAction(
        ac.deleteSavedSearchAsync.failure,
        (state, { payload }): CommonStateType => ({
            ...state,
            isSearchDeleting: false,
            searchDeleteError: payload,
        }),
    )
    .handleAction(
        ac.toggleFiltersPanelAction,
        (state): CommonStateType => ({
            ...state,
            isExpanded: !state.isExpanded,
        }),
    )
    .handleAction(
        ac.clearFiltersModalToggledAction,
        (state, { payload }): CommonStateType => ({
            ...state,
            isClearFiltersModalOpened: payload,
        }),
    )
    .handleAction(
        ac.deleteSavedSearchModalToggledAction,
        (state, { payload }): CommonStateType => ({
            ...state,
            isSavedSearchDeleteModalOpened: !state.isSavedSearchDeleteModalOpened,
            savedSearchToBeDeleted: payload,
        }),
    )
    .handleAction(
        ac.saveSearchModalToggledAction,
        (state, { payload }): CommonStateType => ({
            ...state,
            saveSearchModal: {
                ...payload,
                saveMode: payload.saveMode ?? INITIAL_ADVANCED_SEARCH_COMMON_STATE.saveSearchModal.saveMode,
            },
        }),
    )
    .handleAction(
        ac.saveSearchTriggerModalToggledAction,
        (state, { payload }): CommonStateType => ({
            ...state,
            isSaveSearchTriggerModalOpen: payload,
        }),
    )
    .handleAction(
        ac.fetchSearchQuota.request,
        (state): CommonStateType => ({
            ...state,
            isSearchQuotaLoading: true,
        }),
    )
    .handleAction(
        ac.fetchSearchQuota.success,
        (state, { payload }): CommonStateType => ({
            ...state,
            isSearchQuotaLoading: false,
            searchQuota: {
                total: payload.total,
                remaining: payload.remaining,
                used: payload.used,
                isAvailable: payload.isAvailable,
                isUnlimited: payload.isUnlimited,
                interval: payload.interval,
            },
        }),
    )
    .handleAction(
        ac.fetchSearchQuota.failure,
        (state): CommonStateType => ({
            ...state,
            isSearchQuotaLoading: false,
        }),
    );

const advancedSearchReducer = combineReducers<AdvancedSearchState>({
    common: commonReducer,
    companySearchResults: companySearchResultsReducer,
    contactsSearchResults: contactsSearchReducer,
    contactsSearchProfiles: profilesReducer,
});

export default advancedSearchReducer;
